import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { formatTime } from '@/Pages/Operations/shared';
import { Field, Select } from '@/components/ui/field';
import type { VisitCheck, VisitRow } from '../lib/visits';

interface VisitPickerProps {
  visits: VisitRow[];
  sessionId: string;
  onChange: (sessionId: string) => void;
  /** The bill the chosen visit is running, so the operator sees where it lands. */
  check: VisitCheck | null;
  loading?: boolean;
  /** Shown under the select — e.g. what a package would cover. */
  hint?: string;
}

/**
 * Which visit — the one question that used to be two.
 *
 * The party→guest pair is gone with Party, and what took its place is not a
 * narrower version of the same thing: a bill belongs to one visit, so naming the
 * visit names the bill, and there is no longer a person in between to attribute
 * a charge to. Shared by the resource start dialog, the kids-area check-in and
 * the package sale, so all three name a bill the same way.
 *
 * The resolved check is stated rather than left implied. A visit whose master
 * check has been closed can still be picked and will still refuse the write, and
 * an operator who is told which bill they are about to charge can see that
 * coming.
 */
export function VisitPicker({
  visits,
  sessionId,
  onChange,
  check,
  loading,
  hint,
}: VisitPickerProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="space-y-2">
      <Field label={t('spaces.visits.label')} hint={hint}>
        <Select value={sessionId} onChange={(event) => onChange(event.currentTarget.value)}>
          <option value="">{t('spaces.visits.none')}</option>
          {visits.map((visit) => (
            <option key={visit.id} value={visit.id}>
              {visit.table
                ? t('spaces.visits.atTable', {
                    number: visit.table.number,
                    count: visit.guestCount,
                  })
                : t('spaces.visits.noTable', {
                    time: formatTime(visit.openedAt, i18n.language),
                  })}
            </option>
          ))}
        </Select>
      </Field>

      {sessionId && !loading && (
        <p className="flex items-center gap-1.5 text-xs text-ink-muted">
          <Receipt className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {check
            ? t('spaces.visits.billsTo', { number: check.number })
            : t('spaces.visits.noOpenBill')}
        </p>
      )}
    </div>
  );
}
